/**
 * Authentication Store
 *
 * Manages user authentication state using Pinia.
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authApi } from '@/api/auth'
import { tokenStorage, impersonationStorage } from '@/api'
import type { User, UserRole, LoginRequest } from '@/types'
import { getErrorMessage } from '@/utils/error'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)

  // Getters
  const isAuthenticated = computed(() => !!user.value)
  const isImpersonation = computed(() => impersonationStorage.isActive())
  const userRole = computed<UserRole | null>(() => user.value?.role ?? null)
  const companyId = computed(() => user.value?.company_id)

  // Permission checks
  const canManageUsers = computed(() => {
    const role = userRole.value
    return role === 'superadmin' || role === 'company_admin'
  })

  const canManageCategories = computed(() => {
    const role = userRole.value
    return role === 'superadmin' || role === 'company_admin'
  })

  const canWriteProducts = computed(() => {
    const role = userRole.value
    return role !== 'analyst'
  })

  const canWritePlans = computed(() => {
    const role = userRole.value
    return role !== 'analyst'
  })

  const canEditPlanField = computed(() => canWritePlans.value)

  const canDeletePlans = computed(() => {
    const role = userRole.value
    return role === 'superadmin' || role === 'company_admin' || role === 'sales_director'
  })

  const canExportReports = computed(() => {
    return isAuthenticated.value // All authenticated users can export
  })

  // Actions
  async function login(credentials: LoginRequest): Promise<void> {
    loading.value = true
    error.value = null

    try {
      const response = await authApi.login(credentials)
      tokenStorage.setTokens(response.access_token, response.refresh_token)

      // Fetch user info
      user.value = await authApi.me()
    } catch (e) {
      error.value = getErrorMessage(e, 'Login failed')
      throw e
    } finally {
      loading.value = false
    }
  }

  async function logout(): Promise<void> {
    try {
      const refreshToken = tokenStorage.getRefreshToken()
      await authApi.logout(refreshToken)
    } catch {
      // Ignore errors on logout
    } finally {
      user.value = null
      if (impersonationStorage.isActive()) {
        impersonationStorage.clear()
      } else {
        tokenStorage.clearTokens()
      }
    }
  }

  async function checkAuth(): Promise<void> {
    const wasImpersonation = impersonationStorage.isActive()
    const hasToken = wasImpersonation || !!tokenStorage.getAccessToken()
    if (!hasToken) {
      user.value = null
      return
    }

    try {
      user.value = await authApi.me()
    } catch {
      user.value = null
      if (wasImpersonation) {
        impersonationStorage.clear()  // no-op if interceptor already cleared it
      } else {
        tokenStorage.clearTokens()
      }
    }
  }

  async function updateProfile(data: {
    fullName?: string
    currentPassword?: string
    newPassword?: string
    phone?: string
    verifiedToken?: string
    messengerType?: 'telegram' | 'whatsapp' | null
    messengerHandle?: string | null
  }): Promise<void> {
    loading.value = true
    error.value = null

    try {
      user.value = await authApi.updateProfile(data)
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Update failed'
      throw e
    } finally {
      loading.value = false
    }
  }

  function clearError(): void {
    error.value = null
  }

  return {
    // State
    user,
    loading,
    error,

    // Getters
    isAuthenticated,
    isImpersonation,
    userRole,
    companyId,
    canManageUsers,
    canManageCategories,
    canWriteProducts,
    canWritePlans,
    canEditPlanField,
    canDeletePlans,
    canExportReports,

    // Actions
    login,
    logout,
    checkAuth,
    updateProfile,
    clearError,
  }
})
